// The per-repository checklist: which checks Repo Governance runs on a repo.
//
// Org admins tick and save; engineers see the same list read-only.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge, Button, Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { listRepoChecks, updateGovernedRepo } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { CheckGroup, GovernedRepoView, RepoCheck } from "@/types/api";

const GROUPS: { key: CheckGroup; label: string }[] = [
  { key: "ai", label: "AI agent files" },
  { key: "security", label: "Security" },
  { key: "hygiene", label: "Repository hygiene" },
];

export const useRepoChecks = () =>
  useQuery({ queryKey: qk.repoChecks, queryFn: listRepoChecks, staleTime: Infinity });

export function ChecklistDialog({
  repo,
  open,
  onOpenChange,
  canEdit,
}: {
  repo: GovernedRepoView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const catalogue = useRepoChecks();
  const [selected, setSelected] = React.useState<Set<string>>(new Set(repo.checks));

  React.useEffect(() => {
    if (open) setSelected(new Set(repo.checks));
  }, [open, repo.checks]);

  const save = useMutation({
    mutationFn: () => updateGovernedRepo(repo.github_repo_id, { checks: [...selected].sort() }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: qk.governedRepos });
      const added = updated.checks.filter((c) => !repo.checks.includes(c));
      toast.success(
        added.length && updated.enabled
          ? "Checklist saved. Re-scanning the repository for the new checks."
          : "Checklist saved.",
      );
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const changed = selected.size !== repo.checks.length || repo.checks.some((c) => !selected.has(c));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Checks for {repo.full_name}</DialogTitle>
          <DialogDescription>
            {canEdit
              ? "Choose what Repo Governance scans in this repository. Changes apply to the next scan and every new pull request."
              : "What Repo Governance scans in this repository. Only org admins can change it."}
          </DialogDescription>
        </DialogHeader>

        {catalogue.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : catalogue.isError ? (
          <ErrorState error={catalogue.error} />
        ) : (
          <div className="space-y-5">
            {GROUPS.map((group) => {
              const checks = (catalogue.data ?? []).filter((c) => c.group === group.key);
              if (!checks.length) return null;
              return (
                <fieldset key={group.key} className="space-y-2">
                  <legend className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </legend>
                  {checks.map((check) => (
                    <CheckRow
                      key={check.key}
                      check={check}
                      checked={selected.has(check.key)}
                      disabled={!canEdit || save.isPending}
                      onChange={(on) => toggle(check.key, on)}
                    />
                  ))}
                </fieldset>
              );
            })}
          </div>
        )}

        <DialogFooter>
          {canEdit ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={!changed || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save checklist"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckRow({
  check,
  checked,
  disabled,
  onChange,
}: {
  check: RepoCheck;
  checked: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = `check-${check.key}`;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:disabled]:cursor-default"
    >
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="space-y-0.5">
        <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {check.label}
          {check.on_demand ? (
            <Badge variant="secondary">on demand</Badge>
          ) : check.full_scan_only ? (
            <Badge variant="secondary">full scans only</Badge>
          ) : null}
        </span>
        <span className="block text-xs text-muted-foreground">{check.description}</span>
      </span>
    </label>
  );
}
